import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import './AvailabilityManager.css';

const AvailabilityManager = () => {
  const { userProfile } = useContext(AuthContext);
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // État du formulaire
  const [formData, setFormData] = useState({
    startTime: '09:00',
    endTime: '18:00',
    lunchBreakStart: '12:00',
    lunchBreakEnd: '13:30',
    isWorkingDay: true,
    hasLunchBreak: true,
    // Nouveaux champs pour les rendez-vous à distance
    remoteOnly: false, // Jour où uniquement des rendez-vous à distance sont proposés
    allowsRemote: true, // Jour où des rendez-vous à distance sont possibles
  });
  
  // Jours de la semaine
  const weekDays = [
    { id: 0, name: 'Lundi' },     // Ajusté pour commencer par lundi (0 = lundi)
    { id: 1, name: 'Mardi' },
    { id: 2, name: 'Mercredi' },
    { id: 3, name: 'Jeudi' },
    { id: 4, name: 'Vendredi' },
    { id: 5, name: 'Samedi' },
    { id: 6, name: 'Dimanche' }   // 6 = dimanche
  ];
  
  // Charger les disponibilités du praticien
// Charger les disponibilités du praticien
useEffect(() => {
  const fetchAvailabilities = async () => {
    if (!userProfile?.practitionerId) return;
    
    setLoading(true);
    
    try {
      console.log("Chargement des disponibilités pour le praticien:", userProfile.practitionerId);
      
      // Récupérer les disponibilités par défaut (type = default)
      const snapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', userProfile.practitionerId)
        .where('type', '==', 'default')
        .get();
      
      console.log("Nombre de disponibilités trouvées:", snapshot.size);
      
      // Convertir les documents en objets
      const availabilityData = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        availabilityData[data.day] = {
          id: doc.id, // Stocker l'ID du document
          ...data
        };
        console.log("Disponibilité trouvée pour le jour", data.day, "avec ID:", doc.id);
      });
      
      // Vérifier d'abord si nous avons besoin de créer des disponibilités par défaut
      const daysNeedingDefaultAvailability = [];
      weekDays.forEach(day => {
        if (!availabilityData[day.id]) {
          daysNeedingDefaultAvailability.push(day.id);
        }
      });
      
      // Si certains jours n'ont pas de disponibilité, en créer dans Firestore
      if (daysNeedingDefaultAvailability.length > 0) {
        console.log("Création de disponibilités par défaut pour les jours:", daysNeedingDefaultAvailability);
        
        // Créer des documents pour chaque jour manquant
        for (const dayId of daysNeedingDefaultAvailability) {
          const defaultData = {
            practitionerId: userProfile.practitionerId,
            type: 'default',
            day: dayId,
            startTime: '09:00',
            endTime: '18:00',
            lunchBreak: { start: '12:00', end: '13:30' },
            hasLunchBreak: true,
            isWorkingDay: [0, 1, 2, 3, 4].includes(dayId), // Lundi à vendredi par défaut (0-4)
            remoteOnly: false,
            allowsRemote: true
          };
          
          try {
            // Créer le document dans Firestore
            const docRef = await firestore.collection('availability').add(defaultData);
            console.log(`Disponibilité par défaut créée pour le jour ${dayId} avec ID: ${docRef.id}`);
            
            // Mettre à jour notre objet de données
            availabilityData[dayId] = {
              id: docRef.id,
              ...defaultData
            };
          } catch (error) {
            console.error(`Erreur lors de la création de la disponibilité par défaut pour le jour ${dayId}:`, error);
          }
        }
      }
      
      // Créer un tableau complet pour tous les jours de la semaine
      const fullAvailabilities = weekDays.map(day => {
        return availabilityData[day.id] || {
          practitionerId: userProfile.practitionerId,
          type: 'default',
          day: day.id,
          startTime: '09:00',
          endTime: '18:00',
          lunchBreak: { start: '12:00', end: '13:30' },
          hasLunchBreak: true,
          isWorkingDay: [0, 1, 2, 3, 4].includes(day.id),
          remoteOnly: false,
          allowsRemote: true
        };
      });
      
      setAvailabilities(fullAvailabilities);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des disponibilités:', error);
      setLoading(false);
    }
  };
  
  fetchAvailabilities();
}, [userProfile]);
  
  // Préremplir le formulaire avec les données du jour sélectionné
  const handleDaySelect = (day) => {
    setSelectedDay(day.day);
    
    // Préremplir le formulaire avec les données du jour sélectionné
    setFormData({
      startTime: day.startTime || '09:00',
      endTime: day.endTime || '18:00',
      lunchBreakStart: day.lunchBreak?.start || '12:00',
      lunchBreakEnd: day.lunchBreak?.end || '13:30',
      isWorkingDay: day.isWorkingDay !== undefined ? day.isWorkingDay : true,
      hasLunchBreak: day.hasLunchBreak !== undefined ? day.hasLunchBreak : true,
      // Initialiser les nouveaux champs pour les rendez-vous à distance
      remoteOnly: day.remoteOnly || false,
      allowsRemote: day.allowsRemote !== undefined ? day.allowsRemote : true,
    });
    
    setEditMode(true);
  };
  
  // Mettre à jour les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Si remoteOnly est activé, allowsRemote doit également être activé
    if (name === 'remoteOnly' && checked) {
      setFormData({
        ...formData,
        remoteOnly: true,
        allowsRemote: true
      });
    } else if (name === 'allowsRemote' && !checked) {
      // Si allowsRemote est désactivé, désactiver également remoteOnly
      setFormData({
        ...formData,
        allowsRemote: false,
        remoteOnly: false
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  // Sauvegarder les modifications
  const handleSaveAvailability = async (e) => {
    e.preventDefault();
    
    if (!userProfile?.practitionerId || selectedDay === null) return;
    
    try {
      // Préparer les données à sauvegarder
      const availabilityData = {
        practitionerId: userProfile.practitionerId,
        type: 'default', // Ajout du type pour être cohérent
        day: selectedDay,
        startTime: formData.startTime,
        endTime: formData.endTime,
        lunchBreak: {
          start: formData.lunchBreakStart,
          end: formData.lunchBreakEnd
        },
        hasLunchBreak: formData.hasLunchBreak,
        isWorkingDay: formData.isWorkingDay,
        // Ajouter les nouveaux champs pour les rendez-vous à distance
        remoteOnly: formData.remoteOnly,
        allowsRemote: formData.allowsRemote
      };
      
      console.log("Sauvegarde des disponibilités:", availabilityData);
      
      // Rechercher si une disponibilité existe déjà pour ce jour
      const existingAvailability = availabilities.find(a => a.day === selectedDay && a.id);
      
      if (existingAvailability && existingAvailability.id) {
        console.log("Mise à jour de la disponibilité existante:", existingAvailability.id);
        // Mettre à jour la disponibilité existante
        await firestore
          .collection('availability')
          .doc(existingAvailability.id)
          .update(availabilityData);
      } else {
        console.log("Création d'une nouvelle disponibilité");
        // Créer une nouvelle disponibilité
        const docRef = await firestore
          .collection('availability')
          .add(availabilityData);
          
        console.log("Nouvelle disponibilité créée avec ID:", docRef.id);
      }
      
      // Mettre à jour l'état local
      const updatedAvailabilities = availabilities.map(a => {
        if (a.day === selectedDay) {
          return {
            ...a,
            ...availabilityData,
            id: existingAvailability?.id // Conserver l'ID existant s'il existe
          };
        }
        return a;
      });
      
      setAvailabilities(updatedAvailabilities);
      setEditMode(false);
      setSelectedDay(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des disponibilités:', error);
      alert(`Erreur lors de la sauvegarde: ${error.message}`);
    }
  };
  
  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedDay(null);
  };
  
  if (loading) {
    return <div className="loading">Chargement des disponibilités...</div>;
  }
  
  return (
    <div className="availability-manager">
      <div className="availability-list">
        <h3>Vos horaires de travail</h3>
        <p>Cliquez sur un jour pour modifier les horaires.</p>
        
        <div className="week-days-list">
          {weekDays.map((day, index) => {
            const availability = availabilities.find(a => a.day === day.id);
            
            return (
              <div 
                key={day.id}
                className={`day-card ${selectedDay === day.id ? 'selected' : ''} 
                           ${!availability?.isWorkingDay ? 'day-off' : ''} 
                           ${availability?.remoteOnly ? 'remote-only-day' : ''}`}
                onClick={() => handleDaySelect(availability)}
              >
                <div className="day-name">{day.name}</div>
                {availability?.isWorkingDay ? (
                  <div className="day-hours">
                    {availability.startTime} - {availability.endTime}
                    {availability.hasLunchBreak ? (
                      <div className="lunch-break">
                        Pause déjeuner: {availability.lunchBreak?.start} - {availability.lunchBreak?.end}
                      </div>
                    ) : (
                      <div className="no-lunch-break">
                        Sans pause déjeuner
                      </div>
                    )}
                    {/* Indicateur pour les rendez-vous à distance */}
                    {availability.remoteOnly ? (
                      <div className="remote-only-indicator">
                        À distance uniquement
                      </div>
                    ) : availability.allowsRemote ? (
                      <div className="allows-remote-indicator">
                        Présentiel & à distance
                      </div>
                    ) : (
                      <div className="no-remote-indicator">
                        Présentiel uniquement
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="day-closed">Fermé</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {editMode && selectedDay !== null && (
        <div className="availability-form">
          <h3>Modifier les horaires - {weekDays.find(d => d.id === selectedDay)?.name}</h3>
          
          <form onSubmit={handleSaveAvailability}>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isWorkingDay"
                  checked={formData.isWorkingDay}
                  onChange={handleInputChange}
                />
                Jour travaillé
              </label>
            </div>
            
            <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
              <label>Heure d'ouverture</label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                disabled={!formData.isWorkingDay}
              />
            </div>
            
            <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
              <label>Heure de fermeture</label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                disabled={!formData.isWorkingDay}
              />
            </div>
            
            {/* Nouvelle section pour les options de rendez-vous à distance */}
            <div className={`form-section remote-options ${!formData.isWorkingDay ? 'disabled' : ''}`}>
              <h4 className="remote-options-title">
                <i className="fas fa-video"></i> Options de consultation à distance
              </h4>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="allowsRemote"
                    checked={formData.allowsRemote}
                    onChange={handleInputChange}
                    disabled={!formData.isWorkingDay}
                  />
                  Autoriser les rendez-vous à distance ce jour
                </label>
              </div>
              
              {formData.allowsRemote && formData.isWorkingDay && (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="remoteOnly"
                      checked={formData.remoteOnly}
                      onChange={handleInputChange}
                      disabled={!formData.isWorkingDay || !formData.allowsRemote}
                    />
                    Uniquement des rendez-vous à distance ce jour
                  </label>
                  <div className="checkbox-hint">
                    Aucun rendez-vous en présentiel ne sera proposé ce jour-là.
                  </div>
                </div>
              )}
            </div>
            
            {/* Pause déjeuner */}
            <div className={`form-group checkbox-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  name="hasLunchBreak"
                  checked={formData.hasLunchBreak}
                  onChange={handleInputChange}
                  disabled={!formData.isWorkingDay}
                />
                Pause déjeuner
              </label>
            </div>
            
            {formData.isWorkingDay && formData.hasLunchBreak && (
              <>
                <div className="form-group-title">Horaires de pause déjeuner</div>
                
                <div className="form-row">
                  <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                    <label>Début</label>
                    <input
                      type="time"
                      name="lunchBreakStart"
                      value={formData.lunchBreakStart}
                      onChange={handleInputChange}
                      disabled={!formData.isWorkingDay || !formData.hasLunchBreak}
                    />
                  </div>
                  
                  <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                    <label>Fin</label>
                    <input
                      type="time"
                      name="lunchBreakEnd"
                      value={formData.lunchBreakEnd}
                      onChange={handleInputChange}
                      disabled={!formData.isWorkingDay || !formData.hasLunchBreak}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="form-actions">
              <button type="button" className="btn btn-cancel" onClick={handleCancelEdit}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AvailabilityManager;